import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { buildLinhasFormatadas, getOperadoraWithFallback } from './TriagemStandardColumns';
import { useUserProfileContext } from '@/contexts/UserProfileContext';
import { equipesDoBko } from '@/lib/fila-permissions';
import { equipesDoLider, filtrarPeloLider } from '../triagem/triagemPedidoUtils';
import { usePedidoMovement } from '@/hooks/usePedidoMovement';
import { depoisDoClique, manterNaTriagem } from '@/components/kanban/triagem/acaoTriagem';

const QUADRO_PRE_ATIVO: string = 'PRE_ATIVO';
/** As filas do Pré-Ativo para onde estes botões mandam a linha. */
const VIZINHAS = ['triagem-ativacao', 'triagem-nota-fiscal', 'triagem-portabilidade', 'triagem-cpf-divergente'];


export interface ItemTriagemAtivacao {
  item_id: string;
  pedido_id: string;
  numero_pedido: string;
  item_seq: number | null;
  cnpj: string;
  /** CNPJ ou CPF — a fila mostra um segmento por vez (`segmentoTriagem.ts`). */
  tipo_pessoa: string | null;
  /** CNPJ ou CPF, como está no banco. Máscara em `formatarDocumento`. */
  documento: string;
  razao_social: string;
  tipo_chip: string;
  telefone: string;
  email: string;
  cliente_id: string;
  entrada_coluna_em: string;
  status_id: string;
  coluna_atual: string;
  quadro_atual: string;
  is_first_of_cnpj: boolean;
  /** Por que o item continua nesta fila. Null quando a justificativa é de outra coluna. */
  fila_justificativa: string | null;
  fila_justificativa_em: string | null;
  numero_cotacao_operadora: string;
  linhas_formatadas: string[];
  operadora_doadora: string;
  plano_nome: string;
  faturamento_tipo: string;
  agenda_tipo: string;
  data_agendamento: string;
  contato_real_nome: string;
  cessionario_nome: string;
  cessionario_cpf: string;
  produto_id: string;
  produto: string;
  is_portabilidade: boolean;
}

export function useTriagemAtivacao(coluna: 'INSPEÇÃO CONCLUIDA' | 'AGUARDANDO NOTA FISCAL') {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useUserProfileContext();
  const queryKey = coluna === 'INSPEÇÃO CONCLUIDA' ? 'triagem-ativacao' : 'triagem-nota-fiscal';
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'B',
    baseInterval: POLLING_INTERVALS.PESO_B,
  });

  const { data: itens = [], isLoading, refetch } = useQuery({
    queryKey: [queryKey, profile?.id, profile?.funcao],
    queryFn: async () => {
      const equipes = await equipesDoBko(profile);
      let q = supabase
        .from('v_planilha_mae')
        .select('item_id, pedido_id, numero_pedido, item_seq, cnpj, razao_social, tipo_chip, telefone_principal, gestor_telefone, gestor_whatsapp, contato_real_telefone, contato_real_email, cliente_id, entrada_coluna_em, status_id, coluna_atual, quadro_atual, manter_em, numero_cotacao_operadora, linhas_telefone, plano_nome, faturamento_tipo, agenda_tipo, data_agendamento, contato_real_nome, cessionario_nome, cessionario_cpf, ddd, linha, operadora_doadora, bko_portabilidade_id, owner_qualidade_id, produto_id, produto, tipo_pessoa, documento')
        .eq('coluna_atual', coluna)
        .eq('quadro_atual', 'PRE_ATIVO');
      if (equipes) q = q.in('equipe_id', equipes);
      q = filtrarPeloLider(q, profile, await equipesDoLider(profile));
      const { data, error } = await q
        .order('manter_em', { ascending: true, nullsFirst: true })
        .order('cnpj', { ascending: true })
        .order('pedido_id', { ascending: true })
        .order('entrada_coluna_em', { ascending: true });

      if (error) {
        onQueryError(error);
        throw error;
      }

      onQuerySuccess();

      // ⛔ Pré-Ativo inteiro DAS SUAS EQUIPES (Douglas 29/09/2026): sem recorte
      // por dono (owner_qualidade_id), mas nunca equipe alheia.
      const filtered = data || [];

      // A justificativa da fila mora em itens_pedido, fora da v_planilha_mae:
      // é ela que responde por que o item continua parado aqui.
      const itemIds = (filtered as { item_id?: string | null }[]).map(it => it.item_id).filter(Boolean);
      const justificativaPorItem = new Map<string, { texto: string | null; em: string | null; coluna: string | null }>();
      if (itemIds.length > 0) {
        const { data: justificativas } = await supabase
          .from('itens_pedido')
          .select('id, fila_justificativa, fila_justificativa_em, fila_justificativa_coluna')
          .in('id', itemIds as string[]);
        (justificativas || []).forEach(j => justificativaPorItem.set(j.id, {
          texto: j.fila_justificativa ?? null, em: j.fila_justificativa_em ?? null, coluna: j.fila_justificativa_coluna ?? null,
        }));
      }

      // Buscar flag qtd_pn dos produtos envolvidos (set pequeno)
      const produtoIds = Array.from(new Set(filtered.map((it: any) => it.produto_id).filter(Boolean)));
      const pnMap = new Map<string, boolean>();
      if (produtoIds.length > 0) {
        const { data: produtos } = await supabase
          .from('produtos')
          .select('id, qtd_pn')
          .in('id', produtoIds as string[]);
        (produtos || []).forEach((p: any) => pnMap.set(p.id, p.qtd_pn === true));
      }

      let lastCnpj = '';
      const processed: ItemTriagemAtivacao[] = (filtered as any[]).map((item: any) => {
        const isFirst = item.cnpj !== lastCnpj;
        lastCnpj = item.cnpj || '';
        return {
          item_id: item.item_id,
          pedido_id: item.pedido_id,
          numero_pedido: String(item.numero_pedido || ''),
          item_seq: item.item_seq ?? null,
          cnpj: item.cnpj || '',
          tipo_pessoa: item.tipo_pessoa ?? null,
          documento: item.documento || item.cnpj || '',
          razao_social: item.razao_social || '',
          tipo_chip: item.tipo_chip || '-',
          telefone: item.gestor_telefone || item.gestor_whatsapp || item.contato_real_telefone || item.telefone_principal || '',
          email: item.contato_real_email || '',
          cliente_id: item.cliente_id || '',
          entrada_coluna_em: item.entrada_coluna_em || '',
          status_id: item.status_id || '',
          coluna_atual: item.coluna_atual || coluna,
          quadro_atual: item.quadro_atual || 'PRE_ATIVO',
          is_first_of_cnpj: isFirst,
          // Justificativa de OUTRA fila não explica esta: só vale a da coluna atual.
          fila_justificativa: justificativaPorItem.get(item.item_id)?.coluna === (item.coluna_atual || coluna)
            ? justificativaPorItem.get(item.item_id)?.texto ?? null : null,
          fila_justificativa_em: justificativaPorItem.get(item.item_id)?.em ?? null,
          numero_cotacao_operadora: item.numero_cotacao_operadora || '',
          linhas_formatadas: buildLinhasFormatadas(item),
          operadora_doadora: getOperadoraWithFallback(item),
          plano_nome: item.plano_nome || '',
          faturamento_tipo: item.faturamento_tipo || '',
          agenda_tipo: item.agenda_tipo || '',
          data_agendamento: item.data_agendamento || '',
          contato_real_nome: item.contato_real_nome || '',
          cessionario_nome: item.cessionario_nome || '',
          cessionario_cpf: item.cessionario_cpf || '',
          produto_id: item.produto_id || '',
          produto: item.produto || '',
          is_portabilidade: pnMap.get(item.produto_id) === true,
        };
      });
      return processed;
    },
    refetchInterval,
  });

  const { moverPedidoJunto } = usePedidoMovement();
  const origem = coluna === 'INSPEÇÃO CONCLUIDA' ? 'Ativação' : 'Nota Fiscal';

  // Helper: buscar todos os itens da mesma cotação
  const getItensByCotacao = (cotacao: string): ItemTriagemAtivacao[] => {
    if (!cotacao) return [];
    return itens.filter(i => i.numero_cotacao_operadora === cotacao);
  };

  const daCotacao = (itemId: string): ItemTriagemAtivacao[] => {
    const itemClicado = itens.find(i => i.item_id === itemId);
    if (!itemClicado) throw new Error('Item não encontrado');
    const cotacao = itemClicado.numero_cotacao_operadora;
    return cotacao ? getItensByCotacao(cotacao) : [itemClicado];
  };

  /* A cotação anda numa ida por pedido (`fn_mover_pedido_junto`, que grava o
     histórico): todas as linhas ou nenhuma. Antes era um PATCH e um histórico
     por linha, uma esperando a outra. */
  const moverCotacao = async (itemId: string, toColuna: string, rotulo: string) => {
    const itensCotacao = daCotacao(itemId);
    const porPedido = new Map<string, string[]>();
    for (const it of itensCotacao) porPedido.set(it.pedido_id, [...(porPedido.get(it.pedido_id) ?? []), it.item_id]);
    await Promise.all([...porPedido].map(([pedidoId, itensIds]) => moverPedidoJunto({
      pedidoId,
      itensIds,
      quadroOrigem: QUADRO_PRE_ATIVO,
      colunaOrigem: coluna,
      toQuadro: QUADRO_PRE_ATIVO as Parameters<typeof moverPedidoJunto>[0]['toQuadro'],
      toColuna,
      motivo: `Triagem ${origem} - ${rotulo}`,
      origemTriagem: true,
    })));
    return { count: itensCotacao.length, pedidoId: itensCotacao[0].pedido_id, itens: itensCotacao.map(i => i.item_id) };
  };

  // Mover para Aguardando NF — todos da mesma cotação
  const moverParaAguardandoNF = useMutation({
    mutationFn: (itemId: string) => moverCotacao(itemId, 'AGUARDANDO NOTA FISCAL', 'Aguardar NF'),
    onSuccess: ({ count, pedidoId, itens: ids }) => {
      const label = count > 1 ? `${count} itens da mesma cotação movidos` : 'Item movido';
      toast({ title: 'Aguardando NF', description: `${label} para Aguardando Nota Fiscal.` });
      void depoisDoClique(queryClient, VIZINHAS, { pedidoId, itens: ids }, 'sai');
    },
    onError: (error) => {
      toast({ title: 'Erro', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  // Mover para SMS Enviado — todos da mesma cotação.
  // ⛔ O aviso ao cliente NÃO sai daqui (Douglas 24/09/2026): este botão
  // mandava para um n8n desligado — 0 mensagens em 60 dias — e o aviso dizia
  // "enviado". Quem avisa é a régua AVISO_SMS_ANATEL, pela conversa do pedido.
  const moverParaSmsEnviado = useMutation({
    mutationFn: (itemId: string) => moverCotacao(itemId, 'SMS ENVIADO', 'SMS Enviado'),
    onSuccess: ({ count, pedidoId, itens: ids }) => {
      const label = count > 1 ? `${count} itens da mesma cotação movidos` : 'Item movido';
      toast({ title: 'SMS Enviado', description: `${label}. O aviso ao cliente sai pela régua do SMS, na conversa do pedido.` });
      void depoisDoClique(queryClient, VIZINHAS, { pedidoId, itens: ids }, 'sai');
    },
    onError: (error) => {
      toast({ title: 'Erro', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  // Mover para CPF/CNPJ Divergente — todos da mesma cotação
  const moverParaCpfCnpjDivergente = useMutation({
    mutationFn: (itemId: string) => moverCotacao(itemId, 'CPF / CNPJ DIVERGENTE', 'CPF/CNPJ Divergente'),
    onSuccess: ({ count, pedidoId, itens: ids }) => {
      const label = count > 1 ? `${count} itens da mesma cotação movidos` : 'Item movido';
      toast({ title: 'CPF/CNPJ Divergente', description: `${label} para CPF/CNPJ Divergente.` });
      void depoisDoClique(queryClient, VIZINHAS, { pedidoId, itens: ids }, 'sai');
    },
    onError: (error) => {
      toast({ title: 'Erro', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  // Mover para o final da fila (MANTER) — todos da mesma cotação, numa ida
  // (`fn_triagem_manter`). A justificativa já foi gravada pelo diálogo; só desce
  // a linha que ainda está nesta fila.
  const moverParaFinal = useMutation({
    mutationFn: async (itemId: string) => {
      const itensCotacao = daCotacao(itemId);
      const r = await manterNaTriagem({
        pedidoId: itensCotacao[0].pedido_id,
        itens: itensCotacao.map(i => i.item_id),
        coluna,
      });
      return { count: r.mantidos, pedidoId: itensCotacao[0].pedido_id, itens: itensCotacao.map(i => i.item_id) };
    },
    onSuccess: ({ count, pedidoId, itens: ids }) => {
      const label = count > 1 ? `${count} itens movidos` : 'Item movido';
      toast({ title: 'Manter', description: `${label} para o final da fila.` });
      void depoisDoClique(queryClient, [queryKey], { pedidoId, itens: ids }, 'desce');
    },
    onError: (error) => {
      toast({ title: 'Erro', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  return {
    itens,
    isLoading,
    refetch,
    moverParaAguardandoNF: moverParaAguardandoNF.mutateAsync,
    moverParaSmsEnviado: moverParaSmsEnviado.mutateAsync,
    moverParaCpfCnpjDivergente: moverParaCpfCnpjDivergente.mutateAsync,
    moverParaFinal: moverParaFinal.mutateAsync,
    isMovingToAguardandoNF: moverParaAguardandoNF.isPending,
    isMovingToSmsEnviado: moverParaSmsEnviado.isPending,
    isMovingToCpfCnpjDivergente: moverParaCpfCnpjDivergente.isPending,
    isMovingToFinal: moverParaFinal.isPending,
  };
}
